import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  Archive,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  Link as LinkIcon,
  Loader2,
  SkipForward,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  getListLinkReviewQueueQueryKey,
  useLinkDocument,
  useListDocumentTypes,
  useListLinkReviewQueue,
  useSetDocumentLinkReview,
  useUnlinkDocument,
} from "@/api/client-document";
import { AutoMatchReview } from "@/components/sites/linking/auto-match-review";
import { DocumentPreview } from "@/components/sites/linking/document-preview";
import {
  type LinkTarget,
  LinkTargetPicker,
} from "@/components/sites/linking/link-target-picker";
import { PageStatus } from "@/components/page-status";
import { Button } from "@/components/ui/button";
import {
  FloatingActionBar,
  FloatingActionBarDivider,
} from "@/components/ui/floating-action-bar";
import { Skeleton } from "@/components/ui/skeleton";
import { useSiteStore } from "@/stores/site-store";

export const Route = createFileRoute("/dashboard/onboarding/linking")({
  component: LinkingPage,
  staticData: {
    title: "Linking",
  },
});

/**
 * The review queue: one document at a time, with the equipment it might belong
 * to beside it.
 *
 * This is a screen for the exceptions. Auto-matching places most documents when
 * they land, so what reaches here is the residue — filenames that named nothing,
 * or named two things equally well. That framing is why the header leads with
 * how many were already placed: without it, nine documents to review reads as a
 * failure rather than what's left of twelve hundred.
 */
function LinkingPage() {
  const { selectedSiteId } = useSiteStore();
  const queryClient = useQueryClient();
  const [position, setPosition] = useState(0);
  const [selection, setSelection] = useState<LinkTarget | null>(null);

  const queueParams = useMemo(
    () => ({ siteId: selectedSiteId ?? "" }),
    [selectedSiteId],
  );
  const { data, isLoading } = useListLinkReviewQueue(queueParams, {
    query: { enabled: !!selectedSiteId },
  });
  const { data: documentTypes } = useListDocumentTypes(
    { siteId: selectedSiteId ?? "" },
    { query: { enabled: !!selectedSiteId } },
  );

  const documents = useMemo(() => data?.documents ?? [], [data]);
  const counts = data?.counts;

  // Acting on a document removes it from the queue, so the one after it slides
  // into the same position. Clamping rather than advancing is what makes the
  // list feel like it's being worked through rather than jumping around.
  const index = Math.min(position, Math.max(documents.length - 1, 0));
  const current = documents[index];

  // The catalogue owns the human names, so a type reads the same here as it
  // does in the upload flow and on the equipment page.
  const labelFor = useCallback(
    (type: string) =>
      (documentTypes?.types ?? []).find((entry) => entry.type === type)
        ?.label ?? type,
    [documentTypes],
  );

  const typeLabel = current ? labelFor(current.document_type) : "";

  const refreshQueue = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: getListLinkReviewQueueQueryKey(queueParams).slice(0, 1),
      }),
    [queryClient, queueParams],
  );

  const { mutateAsync: linkDocument, isPending: linking } = useLinkDocument();
  const { mutateAsync: unlinkDocument } = useUnlinkDocument();
  const { mutateAsync: setLinkReview, isPending: dismissing } =
    useSetDocumentLinkReview();

  const settle = useCallback(async () => {
    setSelection(null);
    await refreshQueue();
  }, [refreshQueue]);

  const handleLink = async () => {
    if (!current || !selection) return;
    const { ownerIds, tag, groupSize } = selection;
    const documentId = current.id;

    await linkDocument({
      id: documentId,
      data: { purpose: "equipment_document", owner_ids: ownerIds },
    });
    await settle();

    toast.success(
      groupSize > 1
        ? `Linked to ${tag} and ${groupSize - 1} other tag${groupSize === 2 ? "" : "s"}`
        : `Linked to ${tag}`,
      {
        // The queue moves a document at a time and the obvious mistake is
        // attaching a certificate to the wrong tag. Without this the only way
        // back would be deleting the file, which takes the bytes with it.
        action: {
          label: "Undo",
          onClick: async () => {
            await Promise.all(
              ownerIds.map((ownerId) =>
                unlinkDocument({
                  id: documentId,
                  params: { purpose: "equipment_document", ownerId },
                }),
              ),
            );
            await refreshQueue();
            toast.success("Link removed");
          },
        },
      },
    );
  };

  const handleNotNeeded = async () => {
    if (!current) return;
    const documentId = current.id;

    await setLinkReview({ id: documentId, data: { dismissed: true } });
    await settle();

    toast.success("Put aside as not needed", {
      action: {
        label: "Undo",
        onClick: async () => {
          await setLinkReview({ id: documentId, data: { dismissed: false } });
          await refreshQueue();
          toast.success("Back in the queue");
        },
      },
    });
  };

  // Undoing an automatic match puts the document back in the queue, where it
  // would have been if the filename hadn't matched. That is the point of
  // showing them: a match you can't reverse isn't really reviewable.
  const handleUndoAutoMatch = async (
    documentId: string,
    equipmentId: string,
  ) => {
    await unlinkDocument({
      id: documentId,
      params: { purpose: "equipment_document", ownerId: equipmentId },
    });
    await settle();
    toast.success("Match undone — the document is back in the queue");
  };

  const handleSkip = () => {
    setSelection(null);
    // Deliberately not persisted: a skip that survived a reload would be
    // indistinguishable from "not needed", and the queue would quietly shrink
    // without a decision having been made.
    setPosition((at) => (at + 1) % Math.max(documents.length, 1));
  };

  if (!selectedSiteId) {
    return (
      <PageStatus
        variant="empty"
        title="No asset selected"
        description="Choose an asset to review its unlinked documents."
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-6 w-72" />
        <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
          <Skeleton className="h-[32rem]" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (!current) {
    return (
      <PageStatus
        variant="empty"
        icon={CircleCheck}
        title={
          counts?.dismissed
            ? "No documents are waiting to be linked"
            : "Every document is matched"
        }
        description={
          counts?.dismissed
            ? `${counts.dismissed.toLocaleString()} document${
                counts.dismissed === 1 ? " has" : "s have"
              } been set aside as not needing a link. You can still link ${
                counts.dismissed === 1 ? "it" : "them"
              } from the document page.`
            : counts && counts.linked > 0
              ? `All ${counts.total.toLocaleString()} documents are either linked to their equipment or marked as not needing a link.`
              : "Nothing is waiting to be linked. Upload some documents and anything that can't be matched automatically will appear here."
        }
      />
    );
  }

  const busy = linking || dismissing;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-2">
          <h2 className="text-base font-semibold">
            {documents.length.toLocaleString()} document
            {documents.length === 1 ? "" : "s"} to match
          </h2>
          {/* Every document the site holds is accounted for. An earlier version
              said only "auto-matching placed N of M", which left the set-aside
              ones out and made the arithmetic impossible to follow: 9 held, 3
              placed, 2 to review, and no explanation for the other 4. */}
          {counts && counts.total > 0 && (
            <span className="text-muted-foreground text-sm">
              · of {counts.total.toLocaleString()}:{" "}
              {[
                counts.auto_linked > 0 &&
                  `${counts.auto_linked.toLocaleString()} matched automatically`,
                counts.linked - counts.auto_linked > 0 &&
                  `${(counts.linked - counts.auto_linked).toLocaleString()} linked by hand`,
                counts.dismissed > 0 &&
                  `${counts.dismissed.toLocaleString()} set aside`,
              ]
                .filter(Boolean)
                .join(", ")}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            disabled={index === 0}
            onClick={() => setPosition(index - 1)}
          >
            <ChevronLeft className="size-4" aria-hidden />
            Previous
          </Button>
          <span className="text-muted-foreground px-1 text-sm tabular-nums">
            {index + 1} of {documents.length}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={index >= documents.length - 1}
            onClick={() => setPosition(index + 1)}
          >
            Next
            <ChevronRight className="size-4" aria-hidden />
          </Button>
        </div>
      </div>

      {counts && (
        <AutoMatchReview
          siteId={selectedSiteId}
          count={counts.auto_linked}
          undoing={busy}
          onUndo={handleUndoAutoMatch}
          labelFor={labelFor}
        />
      )}

      <div className="grid items-start gap-4 lg:grid-cols-[1.4fr_1fr]">
        {/* Keyed so moving through the queue rebuilds each panel — a viewer
            whose source changes underneath it keeps the previous document's
            scroll position and page number.
            The keys must differ from each other as well as change per document:
            two siblings sharing one key leaves React unable to pair them across
            a render, and it mounts a second panel beside the first instead of
            replacing it. */}
        <DocumentPreview
          key={`preview-${current.id}`}
          documentId={current.id}
          filename={current.filename}
          contentType={current.content_type}
          sizeBytes={current.size_bytes}
        />

        <LinkTargetPicker
          key={`picker-${current.id}`}
          siteId={selectedSiteId}
          filename={current.filename}
          documentTypeLabel={typeLabel}
          selection={selection}
          onSelect={setSelection}
        />
      </div>

      <FloatingActionBar visible>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            className="text-primary-foreground hover:bg-primary-foreground/20 gap-2"
            disabled={busy}
            onClick={handleNotNeeded}
          >
            <Archive className="size-4" aria-hidden />
            Not needed
          </Button>
          <FloatingActionBarDivider />
          <Button
            variant="ghost"
            className="text-primary-foreground hover:bg-primary-foreground/20 gap-2"
            disabled={busy || documents.length < 2}
            onClick={handleSkip}
          >
            <SkipForward className="size-4" aria-hidden />
            Skip for now
          </Button>
          <FloatingActionBarDivider />
          <Button
            variant="secondary"
            className="gap-2"
            disabled={!selection || busy}
            onClick={handleLink}
          >
            {linking ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <LinkIcon className="size-4" aria-hidden />
            )}
            {selection ? `Link to ${selection.tag}` : "Select equipment"}
          </Button>
        </div>
      </FloatingActionBar>
    </div>
  );
}
